import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { GlassPanel } from "./GlassPanel";
import { Star, Send, Loader2, Eye, Copy } from "lucide-react";

/**
 * Google Review request settings — enable/disable, review URL, subject +
 * body template with `{token}` substitutions, live preview, and a "send
 * me a test" button so the admin can eyeball the actual email before
 * enabling for real customers.
 *
 * The review email fires automatically when a job crosses into
 * `sub_status=collected` — no manual send from this UI.
 */
export default function GoogleReviewSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    review_url: "",
    subject: "",
    body_template: "",
    sms_enabled: false,
  });
  const [preview, setPreview] = useState({ subject: "", body_text: "", html: "" });
  const [tokens, setTokens] = useState([]);
  const [testRecipient, setTestRecipient] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/google-review");
        setConfig(data.config);
        setPreview(data.preview);
        setTokens(data.tokens || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load review settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (patch) => {
    const next = { ...config, ...patch };
    setConfig(next);
    setSaving(true);
    try {
      const { data } = await api.put("/settings/google-review", next);
      setPreview(data.preview);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const copyToken = async (t) => {
    try {
      await navigator.clipboard.writeText(t);
      toast.success(`Copied ${t}`);
    } catch { /* clipboard blocked, silent */ }
  };

  const sendTest = async () => {
    if (!config.review_url) {
      toast.error("Set the Google review URL first");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/google-review/test", {
        recipient: testRecipient || undefined,
      });
      if (data.sent) toast.success("Test email sent — check your inbox");
      else toast.error(`Email not sent: ${data.reason || "unknown reason"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <GlassPanel className="!p-6">
        <div className="text-slate-400 text-sm flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      </GlassPanel>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <GlassPanel className="!p-6" data-testid="settings-google-review">
        <div className="mb-5">
          <div className="text-label flex items-center gap-2">
            <Star className="w-3 h-3" /> Customer reviews
          </div>
          <h2 className="font-heading text-2xl text-white mt-1">Google Review Requests</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            When a job is marked <b>Collected</b>, WrapConsole can automatically email the customer a short thank-you and a one-click link to leave a Google review. Fully customisable below.
          </p>
        </div>

        {/* Main enable toggle */}
        <div className={`rounded-3xl border p-5 transition ${
          config.enabled ? "bg-emerald-500/5 border-emerald-400/30" : "bg-white/[0.02] border-white/10"
        }`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-white font-medium">Send review email on collection</div>
              <p className="text-xs text-slate-400 mt-1 max-w-md">
                Fires the email the moment any job transitions to <b>Collected</b>. Silent no-op if the customer has no email on file.
              </p>
            </div>
            <ReviewToggle
              testId="review-enabled-toggle"
              checked={config.enabled}
              onChange={(v) => save({ enabled: v })}
              disabled={saving}
            />
          </div>
        </div>

        {/* URL */}
        <div className="mt-5 space-y-1.5">
          <label className="text-label" htmlFor="review-url">Google review URL</label>
          <input
            id="review-url"
            data-testid="review-url-input"
            type="url"
            placeholder="https://g.page/r/xxxxxxxxxxxx/review"
            value={config.review_url}
            onChange={(e) => setConfig({ ...config, review_url: e.target.value })}
            onBlur={() => save({ review_url: config.review_url })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
          />
          <p className="text-[11px] text-slate-500">
            Get this from Google Business Profile → <em>Share review form</em>.
          </p>
        </div>

        {/* Subject */}
        <div className="mt-5 space-y-1.5">
          <label className="text-label" htmlFor="review-subject">Email subject</label>
          <input
            id="review-subject"
            data-testid="review-subject-input"
            value={config.subject}
            onChange={(e) => setConfig({ ...config, subject: e.target.value })}
            onBlur={() => save({ subject: config.subject })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
          />
        </div>

        {/* Body */}
        <div className="mt-5 space-y-1.5">
          <label className="text-label" htmlFor="review-body">Email body</label>
          <textarea
            id="review-body"
            data-testid="review-body-input"
            rows={9}
            value={config.body_template}
            onChange={(e) => setConfig({ ...config, body_template: e.target.value })}
            onBlur={() => save({ body_template: config.body_template })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30 font-mono"
          />
        </div>

        {/* Token chips */}
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
            Click a token to copy — insert into subject or body
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tokens.map((t) => (
              <button
                key={t.token}
                type="button"
                data-testid={`review-token-${t.token.replace(/[{}]/g, "")}`}
                onClick={() => copyToken(t.token)}
                title={t.hint}
                className="text-[11px] rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white px-2.5 py-1 inline-flex items-center gap-1 font-mono"
              >
                <Copy className="w-2.5 h-2.5" />
                {t.token}
              </button>
            ))}
          </div>
        </div>

        {/* Test send */}
        <div className="mt-6 border-t border-white/5 pt-5">
          <div className="text-label mb-2 flex items-center gap-2">
            <Send className="w-3 h-3" /> Test the actual email
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <input
              data-testid="review-test-recipient"
              type="email"
              placeholder="Send to (defaults to your admin email)"
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              className="flex-1 min-w-[260px] bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-white/30"
            />
            <button
              type="button"
              data-testid="review-test-send"
              disabled={testing}
              onClick={sendTest}
              className="inline-flex items-center gap-1.5 bg-white text-black px-4 py-2 rounded-full text-xs font-semibold hover:bg-slate-100 disabled:opacity-50"
            >
              {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send test
            </button>
          </div>
        </div>
      </GlassPanel>

      {/* Live preview */}
      <GlassPanel className="!p-6" data-testid="settings-google-review-preview">
        <div className="text-label flex items-center gap-2 mb-3">
          <Eye className="w-3 h-3" /> Preview — sample data
        </div>
        <div className="rounded-2xl bg-white text-slate-800 p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Subject</div>
          <div className="font-medium text-slate-900">{preview.subject || <em className="text-slate-400">(empty)</em>}</div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 pt-2">Body</div>
          <div className="whitespace-pre-wrap leading-relaxed text-sm">{preview.body_text}</div>
        </div>
      </GlassPanel>

      {/* SMS placeholder */}
      <GlassPanel className="!p-5" data-testid="settings-google-review-sms">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-white font-medium">Also send an SMS review request</div>
            <p className="text-xs text-slate-400 mt-1 max-w-md">
              Send the same nudge via text when the job is marked collected. Requires Twilio — coming soon.
            </p>
          </div>
          <span className="pill pill-muted text-[10px] whitespace-nowrap">Coming soon</span>
        </div>
      </GlassPanel>
    </div>
  );
}

function ReviewToggle({ testId, checked, onChange, disabled }) {
  return (
    <button
      type="button"
      data-testid={testId}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`shrink-0 w-11 h-6 rounded-full border transition-colors relative ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      } ${checked ? "bg-emerald-500/70 border-emerald-300/50" : "bg-white/10 border-white/20"}`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}
